import * as bootstrap from 'bootstrap';
import jsKit from './jsKit';

const getBootstrap = () => bootstrap;

const getForm = (selector) => document.querySelector(selector);

const getFormBody = (form) => new URLSearchParams(new FormData(form));

const setButtonLoading = (selector, isLoading) => {
  const button = document.querySelector(selector);
  if (!button) return;

  button.classList.toggle('loading', isLoading);
  button.disabled = isLoading;
};

const postForm = async (url, formSelector, options = {}) => {
  const form = getForm(formSelector);
  if (!form) return null;

  const { buttonSelector = null } = options;

  if (buttonSelector) {
    setButtonLoading(buttonSelector, true);
  } else {
    jsKit.setLoading(form, true);
  }

  const data = await jsKit.requestUrl(url, 'urlencoded', 'POST', getFormBody(form));

  if (buttonSelector) {
    setButtonLoading(buttonSelector, false);
  } else {
    jsKit.setLoading(form, false);
  }

  return data;
};

const handleLocationResponse = async (data, formSelector) => {
  if (!data) return;

  if (data.validated) {
    document.location = data.location;
    return;
  }

  jsKit.showErrors(data, formSelector);
};

const setValue = (targetId, sourceId) => {
  const target = document.getElementById(targetId);
  const source = document.getElementById(sourceId);

  if (target && source) {
    target.value = source.value;
  }
};

const setPanelHtml = (selector, html) => {
  const panel = document.querySelector(selector);
  if (panel && html) {
    panel.innerHTML = html;
  }
};

const replacePanelHtml = (selector, html) => {
  const panel = document.querySelector(selector);
  if (panel && html) {
    panel.outerHTML = html;
  }
};

const clearField = (id) => {
  const input = document.getElementById(id);
  if (input) {
    input.value = '';
  }
};

const showAccountModal = async (url) => {
  const popup = document.getElementById('popup_container');
  const content = popup?.querySelector('.modal-content');
  const bootstrap = getBootstrap();
  if (!popup || !content || !bootstrap?.Modal) {
    return;
  }

  const html = await jsKit.requestUrl(url, 'text', 'POST', new URLSearchParams());
  if (!html) {
    return;
  }

  content.innerHTML = html;
  client.initReviewRatings(content);
  popup.addEventListener('shown.bs.modal', () => jsKit.autofocus(popup), { once: true });
  popup.addEventListener('hidden.bs.modal', () => { content.innerHTML = ''; }, { once: true });
  bootstrap.Modal.getOrCreateInstance(popup).show();
};

const parseDelimitedData = (value) => (value ? value.split('||').map((item) => item.trim()) : []);

let chartDependenciesPromise = null;
let dateRangePickerPromise = null;
const chartLibs = {
  Chart: null,
};

const loadChartDependencies = () => {
    if (!chartDependenciesPromise) {
      chartDependenciesPromise = import(/* webpackChunkName: "charts" */ 'chart.js/auto')
      .then((chartMod) => {
        chartLibs.Chart = chartMod?.default || chartMod?.Chart || chartMod || window.Chart;
        return chartLibs;
      });
    }

  return chartDependenciesPromise;
};

const loadDateRangePicker = () => {
  if (!dateRangePickerPromise) {
    dateRangePickerPromise = import(/* webpackChunkName: "date-range-picker" */ './dateRangePicker');
  }

  return dateRangePickerPromise;
};

const setDateRangePickerValue = async (input, value = '') => {
  const dateRangePicker = await loadDateRangePicker();
  dateRangePicker.setDateRangePickerValue(input, value);
};

const initChart = (canvas) => {
  if (!canvas || !chartLibs.Chart || typeof canvas.getContext !== 'function') return;

  const labels = parseDelimitedData(canvas.dataset.chartLabels);
  const values = parseDelimitedData(canvas.dataset.chartValues).map((value) => Number(value));
  const chartType = canvas.dataset.chartType || 'line';

  if (canvas._chartInstance) {
    canvas._chartInstance.destroy();
  }

  const dataset = {
    data: values,
    borderWidth: Number(canvas.dataset.chartBorderWidth || (chartType === 'pie' ? 2 : 3)),
  };

  if (chartType === 'pie' || chartType === 'doughnut') {
    dataset.backgroundColor = parseDelimitedData(canvas.dataset.chartBackgroundColors);
  } else {
    dataset.fill = canvas.dataset.chartFill !== 'false';
    dataset.lineTension = Number(canvas.dataset.chartLineTension || 0.1);
    dataset.borderColor = canvas.dataset.chartBorderColor || '#fdbe33';
    dataset.borderCapStyle = canvas.dataset.chartBorderCapStyle || 'round';
    dataset.pointBackgroundColor = canvas.dataset.chartPointBackgroundColor || '#ffffff';
    dataset.pointBorderColor = canvas.dataset.chartPointBorderColor || '#fdbe33';
    dataset.pointBorderWidth = Number(canvas.dataset.chartPointBorderWidth || 3);
  }

  const ctx = canvas.getContext('2d');
  canvas._chartInstance = new chartLibs.Chart(ctx, {
    type: chartType,
    data: {
      labels,
      datasets: [dataset],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          cornerRadius: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          displayColors: false,
          footerFont: {
            family: "'Ubuntu', 'Arial', sans-serif",
          },
        },
      },
      animation: {
        duration: Number(canvas.dataset.chartAnimationDuration || 1000),
        easing: canvas.dataset.chartAnimationEasing || 'easeInOutSine',
      },
    },
  });
};

const initInvoiceSummary = async () => {
  const chartElements = [...document.querySelectorAll('[data-js-chart]')];

  if (!chartElements.length) {
    return;
  }

  await loadChartDependencies();
  chartElements.forEach((canvas) => initChart(canvas));

  if (chartElements.some((canvas) => canvas.hasAttribute('data-js-wrapped-chart')) && window.swiperWrapped?.init) {
    window.swiperWrapped.init();
  }
};

export { initInvoiceSummary };

const client = {
  copyDeliveryDataToInvoiceData: () => {
    const fields = {
      delivery_full_name: 'invoice_full_name',
      delivery_street_name: 'invoice_street_name',
      delivery_building_number: 'invoice_building_number',
      delivery_apartment_number: 'invoice_apartment_number',
      delivery_postal_code: 'invoice_postal_code',
      delivery_city: 'invoice_city',
      delivery_country_id: 'invoice_country_id',
    };

    Object.entries(fields).forEach(([source, target]) => setValue(target, source));
  },

  setWantsNewsletter: async (e) => {
    const trigger = e.delegateTarget || e.currentTarget;
    const status = trigger.checked ? 1 : 0;

    if (!status && !window.confirm(document.getElementById('newsletter_question')?.textContent || '')) {
      trigger.checked = true;
      return;
    }

    const formData = new FormData();
    formData.set('client_newsletter_form[status]', status);
    await jsKit.requestUrl('/client/setNewsletterSubscriptionStatus', 'json', 'POST', formData);
  },

  registerApiAccess: async (e) => {
    e.preventDefault();

    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/registerApiAccess', '#api_access', { buttonSelector: '#api_access_button' });
    if (!data) {
      return;
    }

    if (!data.validated) {
      jsKit.showErrors(data, '#api_access_details');
      return;
    }

    jsKit.showSuccess(data.success_message);
    setPanelHtml('#api_access_details', data.html);
  },

  toggleXmlSendApplication: (e) => {
    const agreement = e.delegateTarget || e.currentTarget;
    const button = document.getElementById('xml_send_application');
    if (!button) {
      return;
    }

    button.classList.toggle('disabled', !agreement.checked);
    button.disabled = !agreement.checked;
  },

  registerXmlApplication: async (e) => {
    e.preventDefault();

    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/registerXmlApplication', '#xml_application_register', { buttonSelector: '#xml_send_application' });
    if (!data) {
      return;
    }

    if (!data.validated) {
      jsKit.showErrors(data, form);
      return;
    }

    jsKit.showSuccess(data.success_message);
    if (data.reload) {
      window.location.reload();
      return;
    }

    replacePanelHtml('#xml_application_container', data.html);
  },

  revokeXmlApplication: async (e) => {
    e.preventDefault();

    const trigger = e.delegateTarget || e.currentTarget;
    if (!window.confirm(trigger.dataset.jsRevokeXmlApplication || '')) {
      return;
    }

    const formData = new FormData();
    formData.set('xml_application_revoke[confirm]', '1');
    jsKit.setLoading(trigger, true, { disabled: true });
    const data = await jsKit.requestUrl('/client/revokeXmlApplication', 'json', 'POST', formData);
    jsKit.setLoading(trigger, false, { disabled: true });

    if (!data?.validated) {
      jsKit.showErrors(data, 'body');
      return;
    }

    jsKit.showSuccess(data.success_message);
    replacePanelHtml('#xml_application_container', data.html);
  },

  toggleChangeApplicationButton: (e) => {
    const trigger = e.delegateTarget || e.currentTarget;
    const button = document.getElementById('change_app_button');
    if (!button) {
      return;
    }

    button.classList.toggle('disabled', !trigger.checked);
    button.disabled = !trigger.checked;
  },

  changeApplication: async (e) => {
    e.preventDefault();

    const form = document.getElementById('change_app');
    const trigger = e.delegateTarget || e.currentTarget;
    if (!form || !trigger) {
      return;
    }

    jsKit.setLoading(trigger, true, { disabled: true });
    const data = await jsKit.requestUrl('/client/changeApplication/', 'urlencoded', 'POST', new URLSearchParams(new FormData(form)));
    jsKit.setLoading(trigger, false, { disabled: true });

    if (!data?.validated) {
      const modal = document.getElementById('appChange');
      if (data?.critical) {
        getBootstrap()?.Modal.getInstance(modal)?.hide();
        jsKit.showNotificationsPanel(data.error_message, 'error');
        return;
      }

      jsKit.showErrors(data, form);
      return;
    }

    getBootstrap()?.Modal.getInstance(document.getElementById('appChange'))?.hide();
    jsKit.showNotificationsPanel(data.success_message, 'success');
    window.setTimeout(() => {
      window.location = data.redirect;
    }, 1000);
  },

  saveBaseData: async (e) => {
    e.preventDefault();
    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/saveBaseData', '#client_data_form');
    if (!data) {
      return;
    }

    if (data.validated) {
      jsKit.showSuccess(data.success_message);
      return;
    }

    jsKit.showErrors(data, form);
  },

  showChangePassword: async () => {
    jsKit.hideErrors('#client_data_form');
    await showAccountModal('/client/showChangePassword');
  },

  changePassword: async (e) => {
    e.preventDefault();
    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/changePassword', '#change_password_form');
    if (!data) {
      return;
    }

    if (!data.validated) {
      jsKit.showErrors(data, form);
      return;
    }

    const popup = document.getElementById('popup_container');
    getBootstrap()?.Modal.getInstance(popup)?.hide();
    jsKit.showSuccess(data.success_message);
  },

  showChangeEmail: async () => {
    jsKit.hideErrors('#client_data_form');
    await showAccountModal('/client/showChangeEmail/');
  },

  changeEmail: async (e) => {
    e.preventDefault();
    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/sendEmailChangeRequest', '#change_email_form');
    if (!data) {
      return;
    }

    if (data.validated) {
      form.outerHTML = data.html;
      return;
    }

    jsKit.showErrors(data, form);
  },

  loadDeliveryAddresses: async () => {
    const list = document.getElementById('delivery_addresses_list');
    if (!list) {
      return;
    }

    const html = await jsKit.requestUrl('/client/loadDeliveryAddresses', 'text');
    if (html) {
      list.outerHTML = html;
    }
  },

  editDeliveryAddress: async (e) => {
    const trigger = e.delegateTarget || e.currentTarget;
    await showAccountModal(`/client/editDeliveryAddress/${encodeURIComponent(trigger.dataset.jsEditDeliveryAddress || '0')}`);
  },

  saveDeliveryAddress: async (e) => {
    e.preventDefault();
    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/saveDeliveryAddress', '#client_delivery_address_form');
    if (!data) {
      return;
    }

    if (!data.validated) {
      jsKit.showErrors(data, form);
      return;
    }

    await client.loadDeliveryAddresses();
    getBootstrap()?.Modal.getInstance(document.getElementById('popup_container'))?.hide();
  },

  removeDeliveryAddress: async (e) => {
    e.preventDefault();
    const trigger = e.delegateTarget || e.currentTarget;
    if (!window.confirm('Na pewno usunąć ten adres dostawy?')) {
      return;
    }

    const formData = new FormData();
    formData.set('client_remove_delivery_address[id]', trigger.dataset.jsRemoveDeliveryAddress);
    const data = await jsKit.requestUrl('/client/removeDeliveryAddress', 'json', 'POST', formData);
    if (data?.validated) {
      await client.loadDeliveryAddresses();
    } else {
      jsKit.showErrors(data);
    }
  },

  loadInvoiceAddresses: async () => {
    const list = document.getElementById('invoice_addresses_list');
    if (!list) {
      return;
    }

    const html = await jsKit.requestUrl('/client/loadInvoiceAddresses', 'text');
    if (html) {
      list.outerHTML = html;
    }
  },

  editInvoiceAddress: async (e) => {
    const trigger = e.delegateTarget || e.currentTarget;
    await showAccountModal(`/client/editInvoiceAddress/${encodeURIComponent(trigger.dataset.jsEditInvoiceAddress || '0')}`);
  },

  saveInvoiceAddress: async (e) => {
    e.preventDefault();
    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/saveInvoiceAddress', '#client_invoice_address_form');
    if (!data) {
      return;
    }

    if (!data.validated) {
      jsKit.showErrors(data, form);
      return;
    }

    await client.loadInvoiceAddresses();
    getBootstrap()?.Modal.getInstance(document.getElementById('popup_container'))?.hide();
  },

  removeInvoiceAddress: async (e) => {
    e.preventDefault();
    const trigger = e.delegateTarget || e.currentTarget;
    if (!window.confirm('Na pewno usunąć te dane do faktury VAT?')) {
      return;
    }

    const formData = new FormData();
    formData.set('client_remove_invoice_address[id]', trigger.dataset.jsRemoveInvoiceAddress);
    const data = await jsKit.requestUrl('/client/removeInvoiceAddress', 'json', 'POST', formData);
    if (data?.validated) {
      await client.loadInvoiceAddresses();
    } else {
      jsKit.showErrors(data);
    }
  },

  addReview: async (e) => {
    const trigger = e.delegateTarget || e.currentTarget;
    const reviewId = trigger.dataset.jsAddReview;
    if (reviewId) {
      await showAccountModal(`/client/addReview/${encodeURIComponent(reviewId)}`);
    }
  },

  initReviewRatings: (context) => {
    context.querySelectorAll('[data-js-review-rating]').forEach((root) => {
      if (root.dataset.jsReviewRatingInitialized) {
        return;
      }

      const rating = root.querySelector('.rating');
      const score = root.querySelector('[name="client_review_form[score]"]');
      if (!rating || !score) {
        return;
      }

      const setState = (value, state = 'value') => {
        rating.classList.remove(...[1, 2, 3, 4, 5].map((index) => `${state}-${index}`));
        if (value) {
          rating.classList.add(`${state}-${value}`);
        }
      };

      const clearHover = () => setState(null, 'hover');
      root.querySelectorAll('[data-review-rating-value]').forEach((button) => {
        const value = Number(button.dataset.reviewRatingValue);
        button.addEventListener('pointerenter', () => setState(value, 'hover'));
        button.addEventListener('focus', () => setState(value, 'hover'));
        button.addEventListener('click', () => {
          score.value = value;
          setState(value);
          clearHover();
          root.querySelectorAll('[data-review-rating-value]').forEach((item) => {
            item.setAttribute('aria-pressed', String(item === button));
          });
          jsKit.hideErrors(root);
        });
      });
      root.addEventListener('pointerleave', clearHover);
      root.dataset.jsReviewRatingInitialized = 'true';
    });
  },

  saveReview: async (e) => {
    e.preventDefault();
    const form = e.delegateTarget || e.currentTarget;
    const data = await postForm('/client/saveReview', '#client_review_form');
    if (!data) {
      return;
    }

    if (!data.validated) {
      jsKit.showErrors(data, form);
      return;
    }

    const reviewRow = document.getElementById(`review-${data.review}`);
    if (reviewRow) {
      reviewRow.outerHTML = data.html;
    }
    getBootstrap()?.Modal.getInstance(document.getElementById('popup_container'))?.hide();
    jsKit.showSuccess(data.success_message);
  },

  clientCheckEmail: async () => {
    const form = '#pre_register_form';
    const data = await postForm('/client/checkEmail', form, { buttonSelector: '#btn-check-mail' });
    await handleLocationResponse(data, form);
  },

  clientRegister: async () => {
    const form = '#register_form';
    const data = await postForm('/client/register', form, { buttonSelector: '#btn-register' });
    await handleLocationResponse(data, form);
  },

  registerWholesale: async () => {
    const form = '#register_form';
    const data = await postForm('/client/registerWholesale', form, { buttonSelector: '#btn-wholesale-register' });

    if (!data) return;

    if (data.validated) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'b2b_registration_success' });
      setTimeout(() => {
        document.location = data.location;
      }, 100);
      return;
    }

    jsKit.showErrors(data, form);
  },

  toggleWholesaleDeliveryData: () => {
    const copyToDelivery = document.querySelector('#copy_to_delivery');
    const wholesaleDelivery = document.querySelector('#wholesale_delivery');

    if (wholesaleDelivery) {
      wholesaleDelivery.style.display = copyToDelivery && copyToDelivery.checked ? 'none' : '';
    }
  },

  copyWholesaleDeliveryData: () => {
    const fields = {
      delivery_full_name: 'invoice_full_name',
      delivery_street_name: 'invoice_street_name',
      delivery_building_number: 'invoice_building_number',
      delivery_apartment_number: 'invoice_apartment_number',
      delivery_postal_code: 'invoice_postal_code',
      delivery_city: 'invoice_city',
    };

    Object.entries(fields).forEach(([target, source]) => setValue(target, source));
  },

  clientLogin: async () => {
    const form = '#login_form';
    const data = await postForm('/client/login', form, { buttonSelector: '#btn-login' });
    await handleLocationResponse(data, form);
  },

  forgotPassword: async () => {
    const form = '#forgot_password_form';
    const data = await postForm('/client/forgotPassword', form, { buttonSelector: '#btn-forgot-password' });

    if (!data) return;

    if (data.validated) {
      const bootstrap = getBootstrap();
      const modal = document.querySelector('#password_reminder');
      bootstrap?.Modal.getInstance(modal)?.hide();
      jsKit.showNotificationsPanel(data.success_message, 'success');
      return;
    }

    jsKit.showErrors(data, form);
  },

  resetPassword: async () => {
    const form = '#reset_password_form';
    const data = await postForm('/client/resetPassword', form, { buttonSelector: '#btn-reset-password' });
    await handleLocationResponse(data, form);
  },

  addComplaintMessage: async () => {
    const message = document.querySelector('#message-content');
    if (message && message.value === '') {
      message.focus();
      return;
    }

    const form = '#complaint_message_form';
    const data = await postForm('/client/addComplaintMessage', form, { buttonSelector: '#btn-complaint-message' });

    if (!data) return;

    if (data.validated) {
      message.value = '';
      document.querySelector('#complaint-messages-list')?.insertAdjacentHTML('beforeend', data.message);
      return;
    }

    jsKit.showErrors(data, form);
  },

  addComplaint: async () => {
    const product = document.querySelector('#complaint_product_name');
    if (product && product.value === '') {
      product.focus();
      return;
    }

    const message = document.querySelector('#complaint_form #message-content');
    if (message && message.value === '') {
      message.focus();
      return;
    }

    const form = '#complaint_form';
    const data = await postForm('/client/addComplaint', form, { buttonSelector: '#btn-add-complaint' });
    await handleLocationResponse(data, form);
  },

  showPaymentsForOrder: (orderId) => {
    if (orderId) {
      jsKit.openModalJson(`/client/showPayment/${orderId}`);
    }
  },

  searchClientOrders: async () => {
    const form = '#search_client_orders';
    const data = await postForm('/client/searchClientOrders', form);

    if (data?.validated) {
      await client.loadClientOrders(1);
      return;
    }

    jsKit.showErrors(data, form);
  },

  searchClientPackages: async () => {
    const form = '#search_client_packages';
    const data = await postForm('/client/searchClientPackages', form);

    if (data?.validated) {
      await client.loadClientPackages(1);
      return;
    }

    jsKit.showErrors(data, form);
  },

  loadClientPackages: async (page = 1) => {
    const html = await jsKit.requestUrl(`/client/loadClientPackages/${page}`, 'text');
    setPanelHtml('#panel_packages', html);
  },

  loadClientOrders: async (page = 1) => {
    const html = await jsKit.requestUrl(`/client/loadClientOrders/${page}`, 'text');
    setPanelHtml('#panel_orders', html);
  },

  clearSearchClientOrders: async () => {
    const input = document.querySelector('#search_phrase');
    if (input) input.value = '';
    await client.searchClientOrders();
  },

  setClearingParams: async () => {
    const form = '#clearing_form';
    const data = await postForm('/client/setClearingParams', form);

    if (data?.validated) {
      await client.loadClearing();
      return;
    }

    jsKit.showErrors(data, form);
  },

  refreshClearings: async () => {
    const form = '#clearing_form';
    const data = await postForm('/client/refreshClearings', form);

    if (data?.validated) {
      await client.loadClearing();
      return;
    }

    jsKit.showErrors(data, form);
  },

  clearClearingParams: async () => {
    ['document_date', 'document_number'].forEach(clearField);
    await setDateRangePickerValue(document.getElementById('document_date'), '');

    ['payment_safe', 'payment_postpone', 'payment_incoming', 'payment_exceeded'].forEach((id) => {
      const input = document.getElementById(id);
      if (input) input.checked = true;
    });

    await client.setClearingParams();
  },

  loadClearing: async () => {
    const data = await jsKit.requestUrl('/client/loadClearing');

    if (data?.validated) {
      setPanelHtml('#panel_summary', data.summary);
      setPanelHtml('#panel_documents', data.documents);
      const documentDate = document.querySelector('#document_date');
      await setDateRangePickerValue(documentDate, data.document_date);
    }
  },

  changeClearingOrderBy: async (field, direction) => {
    if (!field || !direction) return;

    const data = await jsKit.requestUrl(`/client/changeClearingOrderBy/${field}/${direction}`);
    if (data?.validated) {
      await client.setClearingParams();
    }
  },

  setInvoicesParams: async () => {
    const form = '#invoices_form';
    const data = await postForm('/client/setInvoicesParams', form);

    if (data?.validated) {
      await client.loadInvoices();
      return;
    }

    jsKit.showErrors(data, form);
  },

  clearInvoicesParams: async () => {
    await setDateRangePickerValue(document.getElementById('document_date'), '');
    ['search_phrase', 'search_value_from', 'search_value_to'].forEach(clearField);

    await client.setInvoicesParams();
  },

  loadInvoices: async () => {
    const data = await jsKit.requestUrl('/client/loadInvoices');

    if (data?.validated) {
      setPanelHtml('#panel_summary', data.summary);
      setPanelHtml('#panel_documents', data.documents);
      const documentDate = document.querySelector('#document_date');
      await setDateRangePickerValue(documentDate, data.document_date);
      await initInvoiceSummary();
    }
  },

  showAddDeliveryLetter: (orderId) => {
    if (orderId) {
      jsKit.openModalJson(`/client/showAddDeliveryLetter/${orderId}`, { keyboard: false, backdrop: 'static' });
    }
  },

  addDeliveryLetter: async () => {
    const form = getForm('#order_delivery_letter');
    const button = document.querySelector('#add-delivery-letter-button');

    if (!form) return;

    if (button) button.disabled = true;

    const response = await jsKit.requestUrl('/client/addDeliveryLetter/', 'json', 'POST', new FormData(form));

    if (button) button.disabled = false;
    if (!response) return;

    if (response.validated) {
      const bootstrap = getBootstrap();
      const popup = document.getElementById('popup_container');
      bootstrap?.Modal.getInstance(popup)?.hide();
      setPanelHtml(`[data-sp-element="delivery-${response.id}"]`, response.content);
      jsKit.showNotificationsPanel(response.message, 'success');
      return;
    }

    jsKit.showErrors(response, '#order_delivery_letter');
  },

  clearPreviousInput: (e) => {
    const input = e.delegateTarget.previousElementSibling;
    if (input && 'value' in input) {
      input.value = '';
    }
  },

  initInvoiceSummary,
};

export const registerClientEvents = () => {
  const events = [
    { dataItem: 'jsClientCheckEmail', handler: client.clientCheckEmail, eventName: 'submit' },
    { dataItem: 'jsSetWantsNewsletter', handler: client.setWantsNewsletter, eventName: 'change' },
    { dataItem: 'jsRegisterApiAccess', handler: client.registerApiAccess, eventName: 'submit' },
    { dataItem: 'jsToggleXmlSendApplication', handler: client.toggleXmlSendApplication, eventName: 'change' },
    { dataItem: 'jsRegisterXmlApplication', handler: client.registerXmlApplication, eventName: 'submit' },
    { dataItem: 'jsRevokeXmlApplication', handler: client.revokeXmlApplication },
    { dataItem: 'jsToggleChangeApplicationButton', handler: client.toggleChangeApplicationButton, eventName: 'change' },
    { dataItem: 'jsChangeApplication', handler: client.changeApplication },
    { dataItem: 'jsSaveBaseData', handler: client.saveBaseData, eventName: 'submit' },
    { dataItem: 'jsShowChangePassword', handler: client.showChangePassword },
    { dataItem: 'jsChangePassword', handler: client.changePassword, eventName: 'submit' },
    { dataItem: 'jsShowChangeEmail', handler: client.showChangeEmail },
    { dataItem: 'jsChangeEmail', handler: client.changeEmail, eventName: 'submit' },
    { dataItem: 'jsEditDeliveryAddress', handler: client.editDeliveryAddress },
    { dataItem: 'jsSaveDeliveryAddress', handler: client.saveDeliveryAddress, eventName: 'submit' },
    { dataItem: 'jsRemoveDeliveryAddress', handler: client.removeDeliveryAddress },
    { dataItem: 'jsEditInvoiceAddress', handler: client.editInvoiceAddress },
    { dataItem: 'jsSaveInvoiceAddress', handler: client.saveInvoiceAddress, eventName: 'submit' },
    { dataItem: 'jsRemoveInvoiceAddress', handler: client.removeInvoiceAddress },
    { dataItem: 'jsAddReview', handler: client.addReview },
    { dataItem: 'jsSaveReview', handler: client.saveReview, eventName: 'submit' },
    { dataItem: 'jsClientRegister', handler: client.clientRegister, eventName: 'submit' },
    { dataItem: 'jsRegisterWholesale', handler: client.registerWholesale, eventName: 'submit' },
    { dataItem: 'jsClientLogin', handler: client.clientLogin, eventName: 'submit' },
    { dataItem: 'jsForgotPassword', handler: client.forgotPassword, eventName: 'submit' },
    { dataItem: 'jsResetPassword', handler: client.resetPassword, eventName: 'submit' },
    { dataItem: 'jsAddComplaintMessage', handler: client.addComplaintMessage, eventName: 'submit' },
    { dataItem: 'jsAddComplaint', handler: client.addComplaint, eventName: 'submit' },
    { dataItem: 'jsSearchClientOrders', handler: client.searchClientOrders },
    { dataItem: 'jsSearchClientPackages', handler: client.searchClientPackages },
    {
      dataItem: 'jsDateRangePickerClear',
      handler: async (event) => {
        const { dateRangePickerClear } = await loadDateRangePicker();
        const input = dateRangePickerClear(event);
        if (input?.id === 'dateOrderRangePicker') {
          await client.searchClientOrders();
        }
      },
    },
    { dataItem: 'jsClearSearchClientOrders', handler: client.clearSearchClientOrders },
    { dataItem: 'jsSetClearingParams', handler: client.setClearingParams, eventName: 'submit' },
    { dataItem: 'jsSetClearingParamsClick', handler: client.setClearingParams },
    { dataItem: 'jsRefreshClearings', handler: client.refreshClearings },
    { dataItem: 'jsClearClearingParams', handler: client.clearClearingParams },
    { dataItem: 'jsSetInvoicesParams', handler: client.setInvoicesParams, eventName: 'submit' },
    { dataItem: 'jsSetInvoicesParamsClick', handler: client.setInvoicesParams },
    { dataItem: 'jsClearInvoicesParams', handler: client.clearInvoicesParams },
    { dataItem: 'jsAddDeliveryLetter', handler: client.addDeliveryLetter },
    { dataItem: 'jsToggleWholesaleDeliveryData', handler: client.toggleWholesaleDeliveryData },
    { dataItem: 'jsToggleWholesaleDeliveryDataChange', handler: client.toggleWholesaleDeliveryData, eventName: 'change' },
    { dataItem: 'jsCopyWholesaleDeliveryData', handler: client.copyWholesaleDeliveryData },
    { dataItem: 'jsCopyDeliveryDataToInvoiceData', handler: client.copyDeliveryDataToInvoiceData },
    { dataItem: 'jsClearPreviousInput', handler: client.clearPreviousInput },
    { dataItem: 'jsShowAddDeliveryLetter', handler: (e) => client.showAddDeliveryLetter(e.delegateTarget.dataset.jsShowAddDeliveryLetter) },
    { dataItem: 'jsShowPaymentsForOrder', handler: (e) => client.showPaymentsForOrder(e.delegateTarget.dataset.jsShowPaymentsForOrder) },
    {
      dataItem: 'jsChangeClearingOrderBy',
      handler: (e) => {
        const [field, direction] = jsKit.getDataParts(e.delegateTarget, 'jsChangeClearingOrderBy');
        client.changeClearingOrderBy(field, direction);
      },
    },
  ];

  events.forEach(({ dataItem, handler, eventName }) => {
    jsKit.attachEventByDataSet(dataItem, (e) => {
      e.preventDefault();
      handler(e);
    }, eventName);
  });
};

export default client;
